import moment from "moment-timezone";
import i18n from "../../i18n";
import { getNonAlcoholLabel } from "./get-team-zone";

export const renderSeatNumbers = (
  seat,
  isWithPrefix = true,
  emptyLabel = "",
  isAdmin = false
) => {
  if (!seat) return emptyLabel;
  const isSingle = Number(seat.seatNumberStart) === Number(seat.seatNumberEnd);
  const label = isSingle ? i18n.t('common.seat') : i18n.t('common.seats');
  const prefix = isWithPrefix ? "\u00A0\u2022 " + label + " " : "";
  const numbersLabel = isSingle
    ? `${prefix}${seat.seatNumberStart}`
    : `${prefix}${seat.seatNumberStart} - ${
        isAdmin
          ? seat.seatNumberEnd
          : Number(seat.seatNumberStart) + Number(seat.noOfSeats) - Number(seat.seatsSold) - 1
      }`;
  if (
    (Number(seat.seatNumberStart) > 0) && 
    (seat.seatsSold === 0) &&
    (isAdmin)
  ) {
    return numbersLabel;
  } else if (
    (!isAdmin) &&
    (seat.seatsSold === 0) &&
    (Number(seat.seatNumberStart) > 0)
  ) {
    return numbersLabel;
  } else if (Number(seat.seatNumberStart) > 0 && isAdmin) {
    return numbersLabel;
  }

  return emptyLabel;
};

export const rateFallback = 1.35;

export const translateZoneLabel = (s) => {
  if (!s) return "";
  const str = String(s);
  const translations = [
    { from: "Upper Zone", to: i18n.t("zones.upperZone") },
    { from: "Middle Zone", to: i18n.t("zones.middleZone") },
    { from: "Lower Zone", to: i18n.t("zones.lowerZone") },
    { from: "Press Level", to: i18n.t("zones.pressLevel") },
    { from: "Club Lower", to: i18n.t("zones.clubLower") },
    { from: "Lower Bowl - Balcony", to: i18n.t("zones.lowerBowlBalcony") },
    { from: "Lower Bowl", to: i18n.t("zones.lowerBowl") },
    { from: "Lower Club", to: i18n.t("zones.lowerClub") },
    { from: "Upper Bowl - Balcony", to: i18n.t("zones.upperBowlBalcony") },
    { from: "Upper Bowl", to: i18n.t("zones.upperBowl") },
    { from: "Reserved Seats", to: i18n.t("zones.reservedSeats") },
    { from: "Reserved", to: i18n.t("zones.reserved") },
    { from: "Suite", to: i18n.t("zones.suite") },
    { from: "Second Level", to: i18n.t("zones.secondLevel") },
    { from: "Club Seats", to: i18n.t("zones.clubSeats") },
    { from: "Sports Bar", to: i18n.t("zones.sportsBar") },
    { from: "General Admission", to: i18n.t("zones.generalAdmission") },
    { from: "Premium Zone", to: i18n.t("zones.premiumZone") },
  ];
  const found = translations.find((t) => str.includes(t.from));
  if (!found || !found.to || found.to.startsWith("zones.")) {
    return str;
  }
  return str.replace(found.from, found.to);
};

export const reverseTranslateZoneLabel = (s) => {
  if (!s) return "";
  const str = String(s);
  const translations = [
    { from: "Upper Zone", to: i18n.t("zones.upperZone") },
    { from: "Middle Zone", to: i18n.t("zones.middleZone") },
    { from: "Lower Zone", to: i18n.t("zones.lowerZone") },
    { from: "Press Level", to: i18n.t("zones.pressLevel") },
    { from: "Club Lower", to: i18n.t("zones.clubLower") },
    { from: "Lower Bowl - Balcony", to: i18n.t("zones.lowerBowlBalcony") },
    { from: "Lower Bowl", to: i18n.t("zones.lowerBowl") },
    { from: "Lower Club", to: i18n.t("zones.lowerClub") },
    { from: "Upper Bowl - Balcony", to: i18n.t("zones.upperBowlBalcony") },
    { from: "Upper Bowl", to: i18n.t("zones.upperBowl") },
    { from: "Reserved Seats", to: i18n.t("zones.reservedSeats") },
    { from: "Reserved", to: i18n.t("zones.reserved") },
    { from: "Suite", to: i18n.t("zones.suite") },
    { from: "Second Level", to: i18n.t("zones.secondLevel") },
    { from: "Club Seats", to: i18n.t("zones.clubSeats") },
    { from: "Sports Bar", to: i18n.t("zones.sportsBar") },
    { from: "General Admission", to: i18n.t("zones.generalAdmission") },
    { from: "Premium Zone", to: i18n.t("zones.premiumZone") },
  ];
  const found = translations.find((t) => t.to && !t.to.startsWith("zones.") && str.includes(t.to));
  return found ? str.replace(found.to, found.from) : str;
};

export const translateRowLabel = (row) => {
  if (!row) return "";
  return String(row).replace(/Row/gi, i18n.t("seatsListDesktop.row"));
};


export const shouldAutoCheckWheelchair = ({ row, section }) => {
  if (row) {
    const normalized = String(row).toUpperCase().replace(/\s/g, "");
    if (normalized.includes("WC") || normalized.includes("WCH")) return true;
  }
  if (section) {
    const normalizedSec = String(section).toUpperCase().replace(/\s/g, "");
    if (
      normalizedSec.includes("W111") ||
      normalizedSec.includes("W207") ||
      normalizedSec.includes("W241") ||
      normalizedSec.includes("W240")
    ) {
      return true;
    }
  }
  return false;
};

export const localizeZoneLabel = (label, teamSlug = '') => {
  if (!label) return '';
  const mappings = [
    { re: /\bUpper Zone\b/gi, key: 'zones.upperZone' },
    { re: /\bMiddle Zone\b/gi, key: 'zones.middleZone' },
    { re: /\bLower Zone\b/gi, key: 'zones.lowerZone' },
    { re: /\bPress Level\b/gi, key: 'zones.pressLevel' },
    { re: /\bClub Lower\b/gi, key: 'zones.clubLower' },
    { re: /\bLower Bowl - Balcony\b/gi, key: 'zones.lowerBowlBalcony' },
    { re: /\bLower Bowl\b/gi, key: 'zones.lowerBowl' },
    { re: /\bLower Club\b/gi, key: 'zones.lowerClub' },
    { re: /\bUpper Bowl - Balcony\b/gi, key: 'zones.upperBowlBalcony' },
    { re: /\bUpper Bowl\b/gi, key: 'zones.upperBowl' },
    { re: /\bReserved Seats\b/gi, key: 'zones.reservedSeats' },
    { re: /\bReserved\b/gi, key: 'zones.reserved' },
    { re: /\bSuite\b/gi, key: 'zones.suite' },
    { re: /\bSecond Level\b/gi, key: 'zones.secondLevel' },
    { re: /\bClub Seats\b/gi, key: 'zones.clubSeats' },
    { re: /\bSports Bar\b/gi, key: 'zones.sportsBar' },
    { re: /\bGeneral Admission\b/gi, key: 'zones.generalAdmission' },
    { re: /\bPremium Zone\b/gi, key: 'zones.premiumZone' },
    { re: /\bNo Alcohol\b/gi, key: 'seatsListDesktop.noAlcohol' },
    { re: /\bClub Access\b/gi, key: 'seatsListDesktop.clubAccess' },
  ];
  let out = String(label);
  mappings.forEach(({ re, key }) => {
    const val = i18n.t(key);
    if (val && !val.startsWith("zones.") && !val.startsWith("seatsListDesktop.") && val !== key) {
      out = out.replace(re, val);
    }
  });
  return out;
};




export const getSafeTimezone = (timezone) => {
  if (!timezone) {
    return "America/Edmonton";
  }
  return timezone;
};

export const getLocalizedDateTime = (date, timezone = "America/Edmonton", formatHour = "H:mm") => {
  const parsedDate = !isNaN(Number(date)) ? Number(date) : date;
  const tz = getSafeTimezone(timezone);
  const dateWithTimeZone = moment
    .tz(parsedDate, tz)
    .format("YYYY-MM-DD");
  const timeWithTimeZone = moment.tz(parsedDate, tz).format(formatHour);
  return {
    stringDate: dateWithTimeZone,
    stringTime: timeWithTimeZone,
  };
};


export const handleGameClickHelper = ({ game, zone, history }) => {
  const slug = `${game.homeTeamSlug}-tickets`;
  history.push(`/seats/${slug}/${game.id}?zone=${zone}`);
};








export const isBlueJaysClubAccessSection = (zoneNo) => {
  const normalizedZoneNo = String(zoneNo || '').replace(/^Lower\s+/i, '').trim();
  const clubAccessSections = ['110', '111', '112', '113', '135', '136', '137', '138'];
  return clubAccessSections.includes(normalizedZoneNo);
};

export const isBlueJaysClubAccessRow = (row) => {
  const rowNumber = parseInt(String(row || '').replace(/[^0-9]/g, ''), 10);
  return !isNaN(rowNumber) && rowNumber >= 1 && rowNumber <= 25;
};

export const isBlueJaysClubAccessSeat = (zoneNo, row) => (
  isBlueJaysClubAccessSection(zoneNo) && isBlueJaysClubAccessRow(row)
);

export const renderZoneName = (zone, zoneNo, row, homeTeamSlug, isSpecial=false) => {
  if (
    typeof row === "string" &&
    row.includes("-") &&
    (homeTeamSlug === undefined || typeof homeTeamSlug === "boolean")
  ) {
    isSpecial = typeof homeTeamSlug === "boolean" ? homeTeamSlug : isSpecial;
    homeTeamSlug = row;
    row = undefined;
  }

  const zoneNumber = zoneNo ? zoneNo : "";
  if (homeTeamSlug === "toronto-blue-jays") {
    const normRow = String(row || "").toUpperCase();
    const normZoneNo = String(zoneNo || "").toUpperCase();
    const normZone = String(zone || "").toUpperCase();

    if (
      normRow.startsWith("GA") ||
      normZoneNo.startsWith("GA") ||
      normZone === "GENERAL ADMISSION"
    ) {
      return "GENERAL ADMISSION";
    }
    if (normZone === "TM LOUNGE" || normZone.includes("LOUNGE")) {
      return "TM LOUNGE";
    }

    const letter = String(zone).split(" ").pop();
    const normalizedZoneNo = String(zoneNo || zone).replace(/^Lower\s+/i, '').trim();
    const hasClubAccessNote = /\(Club Access\)/i.test(String(zone));
    const isClubAccessSection = isBlueJaysClubAccessSeat(normalizedZoneNo, row);

    // Map old zone names to new display names based on section number
    if (zone.includes("Upper Zone")) {
      zone = zone.replace("Upper Zone", "Upper");
    } else if (zone.includes("Middle Zone")) {
      zone = zone.replace("Middle Zone", "Middle");
    } else if (zone.includes("Lower Zone")) {
      const zNo = Number(zoneNo);
      if ([101, 102, 103, 104, 105, 142, 143, 144, 145, 146, 147, 148].includes(zNo)) {
        zone = zone.replace("Lower Zone", "Outfield");
      } else if (zNo >= 221 && zNo <= 227) {
        zone = zone.replace("Lower Zone", "Terrace Club");
      } else {
        zone = zone.replace("Lower Zone", "Lower");
      }
    }

    const baseLabel = ["W"].includes(letter)
      ? `${zone.replace(letter, "")}${letter}${zoneNo}`
      : ["A", "B"].includes(letter)
        ? `${zone.replace(letter, "")}${zoneNo}${letter}`
        : `${zone} ${zoneNumber}`.trim();

    if (isClubAccessSection && !hasClubAccessNote) {
      return `${baseLabel} (Club Access)`;
    }

    return baseLabel;
  }
  if (["Suite", "Infield", "Outfield"].some((name) => String(zone).includes(name))) {
    return `${zone}`;
  }
  else if (
    homeTeamSlug === "calgary-flames" ||
    homeTeamSlug === "calgary-hitmen" ||
    homeTeamSlug === "calgary-wranglers" ||
    homeTeamSlug === "calgary-roughnecks"
  ) {
    const normZone = String(zone || "").trim();
    const isSpecialZone = [
      "REST",
      "Platinum Club",
      "Press Level East General Admission",
      "Press Level West General Admission",
    ].some((special) => normZone.toLowerCase().includes(special.toLowerCase()));

    if (isSpecialZone) {
      if (normZone.toLowerCase().includes("platinum")) return "Platinum Club";
      if (/\brest\b/i.test(normZone)) return "REST";
      if (normZone.toLowerCase().includes("east")) return "Press Level East General Admission";
      if (normZone.toLowerCase().includes("west")) return "Press Level West General Admission";
    }

    const secNo = Number(zoneNo || (typeof zone === "string" ? zone.replace(/\D/g, "") : zone)) || 0;
    let resolvedZone = normZone;
    const isGenericOrMissingZone =
      !normZone ||
      normZone === String(zoneNo) ||
      !isNaN(Number(normZone)) ||
      normZone === "Lower Zone" ||
      normZone === "Upper Zone" ||
      normZone === "Lower" ||
      normZone === "Upper";

    if (isGenericOrMissingZone) {
      if (secNo > 0 && secNo < 19) {
        resolvedZone = "Press Level";
      } else if (secNo > 100 && secNo < 115) {
        resolvedZone = "Lower Bowl";
      } else if (secNo > 114 && secNo < 123) {
        resolvedZone = "Lower Club";
      } else if (secNo > 200 && secNo < 229) {
        resolvedZone = "Second Level";
      }
    }

    const cleanZoneNo = zoneNo ? String(zoneNo).replace(/^PL\s*/i, "").trim() : (secNo ? String(secNo) : "");
    return `${resolvedZone} ${cleanZoneNo}`.trim();
  }
  else if (homeTeamSlug === "edmonton-oilers" || homeTeamSlug === "edmonton-wjc") {
    const zoneOilers = zone.replace(/Upper Zone/g, 'Upper Bowl').replace(/Lower Zone/g, 'Lower Bowl');
    let result = `${zoneOilers} ${zoneNo ? zoneNo : ""}`.trim();
    const numericZoneNo = Number(zoneNo);
    const normalizedRow = String(row);
  
    if (isSpecial) {
      const letter = zone.split(" ").pop();
      const letters = ["S", "AA", "BB", "CC", "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "DD", "EE", "FF", "GG", "HH", "JJ", "KK", "LL", "MM", "NN", "OO", "N", "O", "P", "Q", "R", "T", "U", "V", "W", "X", "Y", "Z", "PP", "QQ", "RR"];    
  
      if (letters.includes(letter)) {
        if (zone.includes("Upper Bowl")) {
          result = `Upper Bowl ${letter}${zoneNumber}`;
        } else if (zone.includes("Lower Bowl")) {
          result = `Lower Bowl ${letter}${zoneNumber}`;
        }
      }
    }

    if ((numericZoneNo >= 101 && numericZoneNo <= 104) || (numericZoneNo >= 118 && numericZoneNo <= 121)) {
      result = result.replace(/Lower Bowl/g, 'Lower Club');
    } else if ([107, 109, 113, 115].includes(numericZoneNo) && ["23", "24", "25"].includes(normalizedRow)) {
      result = result.replace(/Lower Bowl/g, 'Ozone Club');
    }
  
    return result;
  } 
  else if (homeTeamSlug === "ottawa-redblacks") {
    return `${zone} ${Number(zoneNo) > 0 ? zoneNo : ""}`.trim();
  } 
  else if (homeTeamSlug === "bc-lions") {
    if (isSpecial){ 
      const letter = String(zone || zoneNo);
      if (zoneNo >= 1 && zoneNo <= 19) {
        return `Lower Zone ${zoneNo}`;
      } else {
        return `${letter}`;
      }
    }
    const zoneLions = String(zone).toLowerCase().includes("middle zone") ? String(zone).replace("Middle Zone", "Club Seats") : zone;
    return `${zoneLions} ${zoneNo ? zoneNo : ""}${getNonAlcoholLabel({zone: zoneLions, zoneNo, gameDetails: {homeTeamSlug}})}`.trim();
  } 
  else if (homeTeamSlug === "toronto-argonauts") {
    const letter = zone.split(" ").pop();
    if (
      ["A"].includes(
        letter
      )
    ) {
      return `${zone.replace(letter, "")}${zoneNo}${letter}`;
    }
  } 
  else if (homeTeamSlug === "edmonton-elks") {
    const letter = zone.split(" ").pop();
    if (
      ["S", "AA", "BB", "CC", "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "DD", "EE", "FF", "GG", "HH", "JJ", "KK", "LL", "MM", "NN", "OO", "N", "O", "P", "Q", "R", "T", "U", "V", "W", "X", "Y", "Z", "PP", "QQ", "RR"].includes(
        letter
      )
    ) {
      if(zoneNo){
        return `${zone}${zoneNo}`;
      } else {
        return `${zone} ${zoneNo ? zoneNo : ""}`.trim();
      }
    }
  } 
  else if (homeTeamSlug === "saskatchewan-roughriders") {
    const letter = zone.split(" ").pop();
    if (
      ["A"].includes(
        letter
      )
    ) {
      return `${zone.replace(letter, "")}${zoneNo}${letter}`;
    } else if (zone.replace(/[^a-zA-Z]+/g, '').toUpperCase() === 'PILCOUNTRYSTANDINGROOMONLY'){
      return `Pil Country Standing Room Only +19 years of age`;
    }
  } 
  else if (homeTeamSlug === "hamilton-tigercats") {
    if (zone.includes("Premium Zone")) {
      return `${zone}${zoneNumber}`;
    }    
  } 
  else if (homeTeamSlug === "winnipeg-blue-bombers") {
    if ((zoneNo >= 106 && zoneNo <= 109) && ( row >= 9 )) {
      return `${zone.replace(/Lower Zone/g, 'Pinnacle Club')} ${zoneNo ? zoneNo : ""}${getNonAlcoholLabel({zone, zoneNo, gameDetails: {homeTeamSlug}})}`.trim();
    }
  } 
  else if (homeTeamSlug === "abbotsford-canucks") {
    const letter = zone.split(" ").pop();
    if (["A", "B"].includes(letter)) {
      return `${zone.replace(letter, "")}${zoneNo}${letter}`;
    }
  } 
  else if (homeTeamSlug === "vancouver-canucks"){
    if (isSpecial){
      const letter = zone.split(" ").pop();
      if (["A", "B"].includes(letter)) {
        return `${zone.replace(letter, "")}${zoneNo}${letter}`;
      }
    }
  } 
  else if (
    homeTeamSlug === "ottawa-senators" ||
    homeTeamSlug === "iihf-canadian-tire-centre" ||
    (homeTeamSlug === "belleville-senators" && isSpecial) ||
    String(zone).includes("Molson Fan Deck")
  ) {
    if (String(zoneNo) === "214" || String(zone).includes("214")) {
      return "214 - Molson Fan Deck";
    }
    if (String(zoneNo) === "215" || String(zone).includes("215")) {
      return "215 - Molson Fan Deck";
    }
  }
  else if (homeTeamSlug === "belleville-senators"){
    if (isSpecial){
      return `${zone} ${zoneNo ? zoneNo : ""}${getNonAlcoholLabel({zone, zoneNo, gameDetails: {homeTeamSlug}})}`.trim();
    }
    const letter = zone.split(" ").pop();
    if (["L"].includes(letter)) {
      if (zone == 'Lower Zone L') {
        return `Lower Zone ${zoneNo}${letter}`;
      } else {
        return `${zone.replace(letter, "")}${zoneNo}${letter}`;
      }
    } else if (["Box"].includes(letter)) {
      if (zone == 'Lower Zone Box') {
        return `Lower Zone ${zoneNo} ${letter}`;
      } else {
        return `${zone.replace(letter, "")}${zoneNo} ${letter}`;
      }
    }
  }
  else if (homeTeamSlug === "montreal-alouettes") {
    const zoneString = String(zone || "").trim();
    const compactZone = zoneString.replace(/\b([A-Za-z]+)\s+(\d+)\b$/, "$1$2");
    const zoneNoString = String(zoneNo || "").trim();
    if (!zoneNoString) {
      return compactZone;
    }
    if (compactZone.endsWith(zoneNoString)) {
      return compactZone;
    }
    return `${compactZone}${zoneNoString}`;
  }
  else if (homeTeamSlug === "montreal-fc") {
    if (zone.toLowerCase().includes("general admission")) {
      return `${zone}${zoneNo ? ` ${zoneNo}` : ""}`;
    }
    if (
      zone === "Premium Zone F" ||
      zone === "Premium Zone T"
    ) {
      return `${zone}${zoneNo ? zoneNo : ""}`;
    }
    return `${zone} ${zoneNo ? zoneNo : ""}`.trim();
  }
  else if (homeTeamSlug === "winnipeg-jets") {
    if (isSpecial) {
      if ((zoneNo >= 106 && zoneNo <= 109) && ( row >= 9 )) {
        return `${zone.replace(/Lower Zone/g, 'Pinnacle Club')} ${zoneNo ? zoneNo : ""}${getNonAlcoholLabel({zone, zoneNo, gameDetails: {homeTeamSlug}})}`.trim();
      }
    }
  }

  return `${zone} ${zoneNo ? zoneNo : ""}${getNonAlcoholLabel({zone, zoneNo, gameDetails: {homeTeamSlug}})}`.trim();
};

export const renderRowName = (zone, rowNo, homeTeamSlug, isSpecial=false) => {
  const zeroPad = (num, places) => String(num).padStart(places, '0');

  if (homeTeamSlug === "toronto-blue-jays") {
    if (String(zone || '').replace(/[^a-zA-Z]+/g, '').toUpperCase() === 'WESTJETFLIGHTDECK') {
      return `GA${zeroPad(String(rowNo || '').replace(/\D/g,''), 2)}`;
    }
  } 
  // Bombers / Grey Cup: Upper Zone L sections are Row A only on frontend displays
  if (["winnipeg-blue-bombers", "grey-cup"].includes(homeTeamSlug) && /^Upper Zone\s+L\b/i.test(String(zone))) {
    return 'A';
  }
  if (homeTeamSlug === "bc-lions" && isSpecial) {
    if (/ga/i.test(rowNo)) {
      return 'GA';
    }
    return String(rowNo || '').replace(/\d*-GA|GA\d*|-GA|GA-/g, 'GA');
  } 
  return rowNo;
};

export const blueJaysMergeSectionZone = (zone) => {
  if (!zone) return "";
  const zoneStr = typeof zone === "object"
    ? `${zone.zone || ""} ${zone.zoneNo || ""}`.trim()
    : String(zone);

  // Extract section number from zone string (e.g., "Lower Zone 101" -> 101)
  const sectionMatch = zoneStr.match(/(\d+)\s*$/);
  const sectionNum = sectionMatch ? parseInt(sectionMatch[1], 10) : 0;

  // Map old zone names to new display names based on section number
  let displayZone;
  if (zoneStr.includes("Upper Zone")) {
    displayZone = zoneStr.replace("Upper Zone", "Upper");
  } else if (zoneStr.includes("Middle Zone")) {
    displayZone = zoneStr.replace("Middle Zone", "Middle");
  } else if (zoneStr.includes("Lower Zone")) {
    if ([101, 102, 103, 104, 105, 142, 143, 144, 145, 146, 147, 148].includes(sectionNum)) {
      displayZone = zoneStr.replace("Lower Zone", "Outfield");
    } else if (sectionNum >= 221 && sectionNum <= 227) {
      displayZone = zoneStr.replace("Lower Zone", "Terrace Club");
    } else {
      displayZone = zoneStr.replace("Lower Zone", "Lower");
    }
  } else {
    displayZone = zoneStr;
  }

  let nameCheck = [
    "WESTJET FLIGHT DECK",
    "TM LOUNGE",
    "CORONA ROOFTOP PATIO",
    "HOME RUN ZONE",
    "LEFT FIELD BALCONY",
    "RIGHT FIELD BLEACHERS",
    "ROGERS LANDING",
    "SCHNEIDERS PORCH",
    "TD PARK SOCIAL",
    "THE CATCH BAR",
    "THE STOP",
    "GENERAL ADMISSION"
  ];
  if (nameCheck.some(o => zoneStr.includes(o))) {
    return `${zoneStr}`;
  }
  return displayZone;
};

export const cflTeamCheck = (teamSlug) => {
  return (
    teamSlug === "bc-lions" ||
    teamSlug === "ottawa-redblacks" ||
    teamSlug === "toronto-argonauts" ||
    teamSlug === "montreal-alouettes" ||
    teamSlug === "calgary-stampeders" ||
    teamSlug === "winnipeg-blue-bombers" ||
    teamSlug === "edmonton-elks" ||
    teamSlug === "saskatchewan-roughriders" ||
    teamSlug === "hamilton-tigercats"
  );
};

export const nhlTeamCheck = (teamSlug) => {
  return (
    teamSlug === "calgary-flames" ||
    teamSlug === "edmonton-oilers" ||
    teamSlug === "toronto-maple-leafs" ||
    teamSlug === "ottawa-senators" ||
    teamSlug === "montreal-canadiens"
  );
};

export const mlsTeamCheck = (teamSlug) => {
  return (
    teamSlug === "vancouver-whitecaps-fc" ||
    teamSlug === "toronto-fc" ||
    teamSlug === "montreal-fc"
  );
}




export const ufcTeamCheck = (teamSlug) => {
  const teams = ["ufc-fight-night"];
  return teams.includes(teamSlug);
}





export const testDataCheck = (game) => {
  const url = window.location.href;
  let isDevelopmentServer = false;
  if (url.includes('development') || url.includes('localhost') || url.includes('/test-seats') || url.includes('/test-checkout')) {
    isDevelopmentServer = true;
  }
  return (isDevelopmentServer && game.testGame) || !game.testGame ? false : true;
};

export const isTicketEvolution = (seat) => {
  if (!seat) return false;
  return Boolean(
    seat.isTicketEvolution === true ||
    (seat.sellerEmail && seat.sellerEmail === "te@fansfirst.ca") ||
    seat.teListingId
  );
};


export const isTicketNetwork = (seat) => {
  if (!seat) return false;
  return Boolean(
    seat.isTicketNetwork === true ||
    (seat.sellerEmail && seat.sellerEmail === "tn@fansfirst.ca") ||
    seat.tnListingId ||
    seat.tnDetails
  );
};

export const isLaterFulfillmentEligible = ({ seat, game, allowlistedTeams, now = moment() }) => {
  if (!seat || !game) return false;
  if (!isTicketEvolution(seat) && !isTicketNetwork(seat)) return false;

  const teams = Array.isArray(allowlistedTeams) && allowlistedTeams.length > 0
    ? allowlistedTeams
    : ["edmonton-wjc"];
  const teamSlug = game.homeTeamSlug;
  if (!teamSlug || !teams.includes(teamSlug)) return false;

  if (!game.date) return false;
  const gameMoment = game.timezone ? moment.tz(game.date, game.timezone) : moment(game.date);
  const nowMoment = moment(now);
  const diffHours = gameMoment.diff(nowMoment, "hours", true);
  if (diffHours < 36) {
    return false;
  }

  return true;
};

export const getLaterFulfillmentCopy = (copySettings, lang = "en") => {
  const isFr = String(lang).toLowerCase().startsWith("fr");
  const defaultTitle = isFr
    ? "Délai de disponibilité des billets"
    : "Ticket Availability Timing";
  const defaultBody = isFr
    ? "Les billets pour cet événement seront disponibles plus près de la date du match. Vous recevrez une notification par courriel dès que vos billets seront prêts à être transférés."
    : "Tickets for this event will become available closer to the event date. You will receive an email notification as soon as your tickets are ready for transfer.";

  if (!copySettings || typeof copySettings !== "object") {
    return { title: defaultTitle, body: defaultBody };
  }

  if (isFr) {
    return {
      title: copySettings.frTitle || defaultTitle,
      body: copySettings.frBody || defaultBody,
    };
  }
  return {
    title: copySettings.enTitle || defaultTitle,
    body: copySettings.enBody || defaultBody,
  };
};

